import { computed, reactive, ref, useContext } from '@nuxtjs/composition-api'
import { load as loadFingerPrint } from '@fingerprintjs/fingerprintjs'
import { Logger } from '../composition/Logger'
import { VerificationTimer } from '../helpers/verificationTimer'

export function useEmailVerification() {
  const ctx = useContext()

  const status = reactive({
    success: false,
    error: false,
    pending: false,
    verified: false,
    sent: false,
  })

  const errorText = ref('')

  const timerMax = ref(60)
  const timer = ref(timerMax.value)
  const verificationTimer = ref(new VerificationTimer<{ email: string }>())

  const setLoadingStatus = () => {
    status.success = false
    status.error = false
    status.pending = true
    errorText.value = ''
  }

  const resetStatus = () => {
    status.success = false
    status.error = false
    status.pending = false
    status.verified = false
    status.sent = false
    errorText.value = ''
  }

  const setupTimer = () => {
    verificationTimer.value.setup(
      () => {
        timer.value = Math.ceil(verificationTimer.value.deltaTime())
      },
      () => {
        timer.value = 0
      }
    )
  }

  const getEmailVerificationCode = async (email, clientIp, message) => {
    setLoadingStatus()

    const fingerPrintAgent = await loadFingerPrint()
    const fingerPrint = await fingerPrintAgent.get()

    try {
      status.pending = true

      if (
        verificationTimer.value.status?.data.email === email &&
        !verificationTimer.value.isExpired
      ) {
        setupTimer()
        status.sent = true
        status.success = true
        return
      }

      const response = await ctx.$api.requests.getEmailVerificationCode({
        fingerPrint: fingerPrint.visitorId,
        email,
        clientIp,
        message: message || '',
      })

      if (response.status === 200) {
        verificationTimer.value.init(timerMax.value, { email })
        setupTimer()
        status.success = true
        status.sent = true
      } else {
        const [{ message } = ''] = response.errors
        errorText.value = message
        status.error = true
      }
    } catch (e) {
      Logger.error(e)
      status.error = true
    } finally {
      status.pending = false
    }
  }

  const sendEmailVerificationCode = async (code, email, clientIp) => {
    setLoadingStatus()
    try {
      const response = await ctx.$api.requests.postEmailVerificationCode({
        code,
        email,
        clientIp,
      })
      if (response.status === 200) {
        status.success = true
        status.verified = true
      } else {
        const [{ message } = {}] = response.errors
        errorText.value = message
        status.error = true
      }
    } catch (e) {
      status.error = true
    } finally {
      status.pending = false
    }
  }

  const sendError = computed(() => {
    return (
      !status.sent &&
      status.error &&
      (errorText.value || 'Ошибка при получении кода')
    )
  })

  const verifyError = computed(() => {
    return (
      status.sent &&
      !status.verified &&
      status.error &&
      (errorText.value || 'Ошибка при подтверждении кода')
    )
  })

  return {
    status,
    errorText,
    timer,
    resetStatus,
    getEmailVerificationCode,
    sendEmailVerificationCode,
    sendError,
    verifyError,
  }
}
